import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import "@/index.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import NavBridge from "@/components/NavBridge";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import StrategyBuilder from "@/pages/StrategyBuilder";
import Backtest from "@/pages/Backtest";
import PaperExecution from "@/pages/PaperExecution";
import TrapDetection from "@/pages/TrapDetection";
import Journal from "@/pages/Journal";
import AuditLog from "@/pages/AuditLog";
import Admin from "@/pages/Admin";
import Brokers from "@/pages/Brokers";
import BrokerCallback from "@/pages/BrokerCallback";
import Settings from "@/pages/Settings";
import SignalFeed from "@/pages/SignalFeed";
import ScannerPerformance from "@/pages/ScannerPerformance";
import Copilot from "@/pages/Copilot";
import Analytics from "@/pages/Analytics";
import PnLCalendar from "@/pages/PnLCalendar";
import PnL from "@/pages/PnL";
import MarketIntelligence from "@/pages/MarketIntelligence";
import EconomicCalendar from "@/pages/EconomicCalendar";
import Watchlist from "@/pages/Watchlist";
import PriceAlerts from "@/pages/PriceAlerts";
import Pricing from "@/pages/Pricing";
import Options from "@/pages/Options";
import ORBWatchlist from "@/pages/ORBWatchlist";
import RegimeSuite from "@/pages/RegimeSuite";
import FAQ from "@/pages/FAQ";
import Blog from "@/pages/Blog";
import Learn from "@/pages/Learn";
import LearnArticle from "@/pages/LearnArticle";
import StrategyGuide from "@/pages/StrategyGuide";
import Landing from "@/pages/Landing";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-page)] text-zinc-400">
        <div className="overline animate-pulse">Initialising cockpit…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Root: show Landing for visitors, Dashboard for logged-in users
function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-page)] text-zinc-400">
        <div className="overline animate-pulse">Initialising cockpit…</div>
      </div>
    );
  }
  return user ? <Dashboard /> : <Landing />;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function App() {
  useEffect(() => {
    document.title = "AlgoForge — AI Trading Cockpit";
    const saved = localStorage.getItem("af-theme") || "dark";
    document.documentElement.classList.remove("dark", "light");
    document.documentElement.classList.add(saved);
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <NavBridge />{/* RU-SCANPERF SP-9: client-side navigation for ScannerLink */}
        <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/" element={<RootRoute />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/strategies" element={<ProtectedRoute><StrategyBuilder /></ProtectedRoute>} />
          <Route path="/backtest" element={<ProtectedRoute><Backtest /></ProtectedRoute>} />
          <Route path="/paper" element={<ProtectedRoute><PaperExecution /></ProtectedRoute>} />
          <Route path="/trap" element={<ProtectedRoute><TrapDetection /></ProtectedRoute>} />
          <Route path="/signals" element={<ProtectedRoute><SignalFeed /></ProtectedRoute>} />
          <Route path="/signals/scanner/:scanner" element={<ProtectedRoute><ScannerPerformance /></ProtectedRoute>} />
          <Route path="/copilot" element={<ProtectedRoute><Copilot /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
          <Route path="/pnl" element={<ProtectedRoute><PnL /></ProtectedRoute>} />
          <Route path="/pnl-calendar" element={<ProtectedRoute><PnLCalendar /></ProtectedRoute>} />
          <Route path="/market" element={<ProtectedRoute><MarketIntelligence /></ProtectedRoute>} />
          <Route path="/calendar" element={<ProtectedRoute><EconomicCalendar /></ProtectedRoute>} />
          <Route path="/journal" element={<ProtectedRoute><Journal /></ProtectedRoute>} />
          <Route path="/audit" element={<Navigate to="/journal?tab=audit" replace />} />
          <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
          <Route path="/brokers" element={<ProtectedRoute><Brokers /></ProtectedRoute>} />
          <Route path="/brokers/callback" element={<ProtectedRoute><BrokerCallback /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/watchlist" element={<ProtectedRoute><Watchlist /></ProtectedRoute>} />
          <Route path="/price-alerts" element={<Navigate to="/watchlist?tab=alerts" replace />} />
          <Route path="/pricing" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />{/* kept for logged-in nav link */}
          <Route path="/options" element={<ProtectedRoute><Options /></ProtectedRoute>} />
          <Route path="/orb" element={<ProtectedRoute><ORBWatchlist /></ProtectedRoute>} />
          <Route path="/suite" element={<ProtectedRoute><RegimeSuite /></ProtectedRoute>} />
          {/* Public routes — no login required */}
          <Route path="/faq" element={<FAQ />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<Blog />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/learn" element={<Learn />} />
          <Route path="/learn/:slug" element={<LearnArticle />} />
          <Route path="/strategy-guide/:slug" element={<StrategyGuide />} />
          <Route path="/about" element={<Landing />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="bottom-right" richColors closeButton duration={5000} />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
